import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Row, Col, Table, Select, Dropdown, Menu,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import TableDeleteButton from './TableDeleteButton';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import DisplayText from '../../../common/text/DisplayText';

import BillingRateSelector from '../../../billingRates/BillingRateSelector';

import { getLuxonRuntimeFromTask } from '../../../helpers/time';
import { getIdMap, isNullOrUndefined } from '../../../helpers/helpers';

import LabourHoursDrawer from './LabourHoursDrawer';
import { currencyParser, currencyFormatter } from '../../../helpers/inputParsers';
import { generateId, getHasColumnPermissions } from '../../formHelpers';
import Permissions from '../../../auth/Permissions';
import { getCalculationTableColumn, getCustomTableColumn, LINE_NUMBER_COLUMN } from './formFieldsHelpers';

const getTotal = (item) => parseFloat((item.rate * item.quantity).toFixed(2));

const getLabourColumnMap = ({
  onUpdateValue,
  isDisplay,
  billingRates = {},
  billingRateNameMap = {},
  defaultBillingRate = {},
  requiredColumns,
  preventEdits,
  divisionUsers = [],
  userMap = {},
}) => ({
  name: {
    title: <div className={requiredColumns && 'form-required-field'}>Name</div>,
    dataIndex: 'name',
    width: 250,
    render: (name, record) => {
      if (isDisplay) return name;
      const {
        [name]: {
          id: billingRateId,
        } = {},
      } = billingRateNameMap;
      return (
        <Row align="middle">
          <BillingRateSelector
            style={{ width: '100%' }}
            isNotDisplay={!isDisplay}
            onlyNames
            billingRateId={record.isCustom ? record.name : billingRateId}
            customEntries={defaultBillingRate.isCustom ? [defaultBillingRate] : []}
            onChange={(value) => {
              const custom = !(value in billingRates);
              let newRate;
              if (custom) {
                newRate = !isNullOrUndefined(record.customRate)
                  ? record.customRate
                  : 0;
              } else {
                newRate = billingRates[value].rate;
              }
              onUpdateValue({
                id: record.id,
                newData: {
                  name: custom ? value : billingRates[value].name,
                  rate: newRate,
                  isCustom: custom,
                  ...(custom ? { customRate: newRate } : {}),
                },
              });
            }}
          />
        </Row>
      );
    },
  },
  rate: {
    title: <div className={requiredColumns && 'form-required-field'}>Rate</div>,
    dataIndex: 'rate',
    width: 100,
    render: (rate, record) => {
      if (isDisplay || preventEdits) return !isNullOrUndefined(rate) ? `${currencyFormatter(rate)} / hr` : '';
      return (
        <Row align="middle">
          <OnTraccrNumberInput
            key={record.id}
            min={0}
            onChange={(value) => onUpdateValue({ id: record.id, newData: { rate: value } })}
            value={rate}
            formatter={currencyFormatter}
            parser={currencyParser}
          />
        </Row>
      );
    },
  },
  quantity: {
    title: <div className={requiredColumns && 'form-required-field'}>Quantity</div>,
    dataIndex: 'quantity',
    width: 100,
    render: (quantity, record) => {
      if (isDisplay || preventEdits) return quantity;
      return (
        <Row align="middle">
          <OnTraccrNumberInput
            min={0}
            onChange={(value) => onUpdateValue({ id: record.id, newData: { quantity: value } })}
            value={quantity}
          />
        </Row>
      );
    },
  },
  total: {
    title: 'Total',
    dataIndex: 'total',
    width: 100,
    render: (total) => {
      const totalText = total ? currencyFormatter(total) : '$0.00';
      if (isDisplay) return totalText;
      return (
        <Row align="middle">
          <DisplayText title={totalText} style={{ marginBottom: 0 }} />
        </Row>
      );
    },
  },
  user: {
    title: <div className={requiredColumns && 'form-required-field'}>User</div>,
    dataIndex: 'userId',
    width: 200,
    render: (userId, record) => {
      if (isDisplay || preventEdits) return record.user;
      return (
        <Row align="middle">
          <Select
            className="OnTraccrSelect"
            style={{ width: 200 }}
            showSearch
            placeholder="Select a User"
            value={userId}
            optionFilterProp="label"
            onChange={(_value, option) => (
              onUpdateValue({
                id: record.id,
                newData: {
                  userId: option.value,
                  user: option.label,
                  position: userMap[option.value]?.position,
                },
              })
            )}
            options={divisionUsers}
          />
        </Row>
      );
    },
  },
  position: {
    title: 'Position',
    dataIndex: 'position',
    width: 150,
    render: (position) => position || '',
  },
});

const labourColumns = ({
  onDelete,
  onUpdateValue,
  columns,
  isDisplay,
  billingRates,
  billingRateNameMap,
  defaultBillingRate,
  requiredColumns,
  preventEdits,
  divisionUsers,
  userMap,
  columnPermissions,
  userPositionId,
  responses,
}) => {
  const cols = [LINE_NUMBER_COLUMN];
  const labourColumnMap = getLabourColumnMap({
    onUpdateValue,
    isDisplay,
    billingRates,
    billingRateNameMap,
    defaultBillingRate,
    requiredColumns,
    preventEdits,
    divisionUsers,
    userMap,
  });
  columns.forEach((col) => {
    if (col.key in labourColumnMap) {
      cols.push(labourColumnMap[col.key]);
    } else if (col.isCustomText) {
      if (!getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })) {
        return;
      }
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        preventEdits,
        requiredColumns,
        responses,
        onChange: (record, value) => onUpdateValue({ id: record.id, newData: { [col.key]: value } }),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        width: 100,
        isDisplay,
      }));
    }
  });
  if (!isDisplay) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }
  return cols;
};

export default function LabourTablePreview({
  title = null,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  setResponses,
  responses = {},
  responding = false,
  projectId,
  customerId,
  configProps: {
    hideAddNewButton,
    requiredColumns,
    preventEdits,
    columnPermissions = {},
  } = {},
  showCondensedView,
  divisions = [],
}) {
  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected = [],
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;

  const [showDrawer, setShowerDrawer] = useState(false);
  const billingRates = useSelector((state) => state.billingRates);
  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const divisionMap = useSelector((state) => state.settings.divisions);

  const billingRateNameMap = useMemo(() => getIdMap(Object.values(billingRates), 'name'), [billingRates]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const defaultBillingRate = useMemo(() => {
    const {
      [projectId]: {
        billingRateId: projectBillingRateId,
        customerId: projectCustomerId = customerId,
        customBillingRate: customProjectBillingRate,
        name: projectName,
      } = {},
    } = projectMap;
    const {
      [projectCustomerId]: {
        billingRateId: customerBillingRateId,
        customBillingRate: customCustomerBillingRate,
        name: customerName,
      } = {},
    } = customers;
    if (projectBillingRateId) return billingRates[projectBillingRateId];
    if (customProjectBillingRate) {
      return {
        name: projectName,
        rate: customProjectBillingRate,
        isCustom: true,
        id: projectName,
      };
    }
    if (customerBillingRateId) return billingRates[customerBillingRateId];
    return {
      name: customerName,
      rate: customCustomerBillingRate,
      isCustom: true,
      id: customerName,
    };
  }, [projectMap, projectId, customerId, customers, billingRates]);

  const divisionUsers = useMemo(() => {
    const allUsers = divisions?.map((divId) => {
      const {
        [divId]: {
          users: divUserSet = new Set(),
        } = {},
      } = divisionMap;
      return Array.from(divUserSet);
    }) ?? [];
    const userSet = new Set(allUsers.flat());
    const filteredUsers = users.filter((user) => user.active && userSet.has(user.id));

    return filteredUsers.map((user) => ({
      label: user.name,
      value: user.id,
    }));
  }, [users, divisionMap, divisions]);

  const onAddClicked = useCallback(() => setShowerDrawer(true), []);
  const closeDrawer = useCallback(() => setShowerDrawer(false), []);

  const updateResponses = useCallback((newData = {}) => {
    setResponses({
      ...responses,
      [id]: {
        ...(responses[id]),
        ...newData,
      },
    });
  }, [responses, id, setResponses]);

  const onAddNewClicked = useCallback(() => {
    const newEntry = {
      id: generateId(),
    };
    if (defaultBillingRate) {
      newEntry.name = defaultBillingRate.name;
      newEntry.rate = defaultBillingRate.rate;
      newEntry.isCustom = defaultBillingRate.isCustom;
    }
    const newSelected = selected.concat([newEntry]);
    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [selected, previewProps, responding, columns, updateResponses, defaultBillingRate]);

  const onSubmit = useCallback((selectedTasks) => {
    const runtimes = selectedTasks.map((task) => {
      const newEntry = {
        id: task.id,
        quantity: parseFloat(getLuxonRuntimeFromTask(task, { rawMs: true }).as('hours').toFixed(1)),
        userId: task.userId,
        user: userMap[task.userId]?.name,
        position: userMap[task.userId]?.position,
      };
      if (defaultBillingRate) {
        newEntry.name = defaultBillingRate.name;
        newEntry.rate = defaultBillingRate.rate;
        newEntry.total = getTotal(newEntry);
        newEntry.isCustom = defaultBillingRate.isCustom;
      }
      return newEntry;
    });
    const newSelected = selected.concat(runtimes);
    if (responding) {
      updateResponses({ values: newSelected, columns });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }

    setShowerDrawer(false);
  }, [
    selected,
    responding,
    previewProps,
    updateResponses,
    columns,
    defaultBillingRate,
    userMap,
  ]);

  const onDelete = useCallback((deletedId) => {
    const newSelected = selected.filter((item) => item.id !== deletedId);
    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: selected.filter((item) => item.id !== deletedId),
      });
    }
  }, [previewProps, selected, updateResponses]);

  const onUpdateValue = useCallback(({ id: changedId, newData = {} }) => {
    const newSelected = [...selected].map((item) => {
      if (item.id !== changedId) return item;
      const newItem = {
        ...item,
        ...newData,
      };
      if (newItem.isCustom && !isNullOrUndefined(newItem.rate)) {
        newItem.customRate = newItem.rate;
      }
      if (newItem.rate && (newItem.quantity || newItem.quantity === 0)) {
        newItem.total = getTotal(newItem);
      }
      return newItem;
    });
    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [selected, responding, updateResponses, previewProps, columns]);

  const tableColumns = useMemo(() => (
    labourColumns({
      onDelete,
      onUpdateValue,
      columns,
      isDisplay,
      billingRates,
      billingRateNameMap,
      defaultBillingRate,
      requiredColumns,
      preventEdits,
      divisionUsers,
      userMap,
      columnPermissions,
      userPositionId,
      responses,
    })
  ), [
    columns,
    isDisplay,
    onDelete,
    onUpdateValue,
    billingRates,
    billingRateNameMap,
    defaultBillingRate,
    requiredColumns,
    preventEdits,
    divisionUsers,
    userMap,
    columnPermissions,
    userPositionId,
    responses,
  ]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);

  return (
    <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Row justify='space-between' style={{ marginBottom: 10, width: '100%' }}>
        <Col>
          {title}
        </Col>
      {!isDisplay && (
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          overlay={(
            <Menu
              onClick={({ key }) => {
                switch (key) {
                  case 'addFromTimeCards':
                    onAddClicked();
                    break;
                  case 'addNew':
                    onAddNewClicked();
                    break;
                }
              }}
            >
              {!hideAddNewButton && (
                <Menu.Item key="addNew">
                  Add Row
                </Menu.Item>
              )}
              <Menu.Item key="addFromTimeCards">
                Add from Time Cards
              </Menu.Item>
            </Menu>
          )}
        >
          <OnTraccrButton
            title="Add"
            icon={<DownOutlined />}
          />
        </Dropdown>
      )}
      </Row>
      { !showCondensedView || dataSource?.length ? (
        <Table
          className='form-table-preview'
          style={{ width: '100%', overflow: 'auto' }}
          columns={tableColumns}
          size="small"
          pagination={false}
          dataSource={dataSource}
        />
      ) : (
        <DisplayText title="No Labour Hours" style={{ marginBottom: 0 }} />
      )}
      {!isDisplay && (
        <LabourHoursDrawer
          visible={showDrawer}
          onClose={closeDrawer}
          onSubmit={onSubmit}
        />
      )}
    </Row>
  );
}

LabourTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    dataIndex: PropTypes.string,
    key: PropTypes.string,
  })),
  responding: PropTypes.bool,
  responses: PropTypes.objectOf(PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      rate: PropTypes.number,
      quantity: PropTypes.number,
      total: PropTypes.number,
    })),
    columns: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      dataIndex: PropTypes.string,
      key: PropTypes.string,
    })),
  })),
  setResponses: PropTypes.func,
  id: PropTypes.string,
  isDisplay: PropTypes.bool,
  previewProps: PropTypes.shape({
    selected: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      rate: PropTypes.number,
      quantity: PropTypes.number,
      total: PropTypes.number,
    })),
  }),
  setPreviewProps: PropTypes.func,
  projectId: PropTypes.string,
  customerId: PropTypes.string,
  configProps: PropTypes.shape({
    hideAddNewButton: PropTypes.bool,
    requiredColumns: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
};

LabourTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  responding: false,
  responses: {},
  setPreviewProps: null,
  setResponses: null,
  id: null,
  isDisplay: false,
  projectId: null,
  customerId: null,
  configProps: {},
  showCondensedView: false,
  divisions: [],
};
